import { PieChart as PieChartIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { cn } from "@utils/design-utils";
import type { ReactNode } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const MAX_LABEL_LINE_LENGTH = 14;

const formatLabelLines = (text: string) => {
  if (!text) {
    return [];
  }

  const words = text.split(" ");
  const lines: string[] = [""];

  words.forEach((word) => {
    const currentLine = lines[lines.length - 1];
    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (candidate.length <= MAX_LABEL_LINE_LENGTH || !currentLine) {
      lines[lines.length - 1] = candidate;
    } else if (lines.length === 1) {
      lines.push(word);
    } else {
      lines[lines.length - 1] = `${lines[lines.length - 1]}…`;
    }
  });

  if (lines.length > 2) {
    lines.length = 2;
  }

  if (lines.length === 1) {
    lines.push("");
  }

  return lines as [string, string];
};

const renderLabel = (props: any, fontScale = 1) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    percent,
    fill,
  } = props;

  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const textAnchor = x > cx ? "start" : "end";
  const [lineOne, lineTwo] = formatLabelLines(name);
  const percentText = `${(percent * 100).toFixed(0)}%`;
  const secondLine = lineTwo ? `${lineTwo} ${percentText}` : percentText;
  const textColor = fill || "#1f2937";

  const backgroundWidth = 120;
  const backgroundHeight = 44;
  const padding = 8;
  const textStartX = textAnchor === "start" ? x + padding : x - padding;
  const rectX = textAnchor === "start" ? x : x - backgroundWidth;
  const rectY = y - backgroundHeight / 2;

  return (
    <g className="select-none">
      <rect
        x={rectX}
        y={rectY}
        width={backgroundWidth}
        height={backgroundHeight}
        fill="#ffffff"
        opacity={0.88}
        rx={6}
        ry={6}
      />
      <text
        x={textStartX}
        y={y - 6}
        fill={textColor}
        textAnchor={textAnchor === "start" ? "start" : "end"}
        fontSize={18 * fontScale}
        fontWeight="600"
      >
        {lineOne}
      </text>
      <text
        x={textStartX}
        y={y + 12}
        fill={textColor}
        textAnchor={textAnchor === "start" ? "start" : "end"}
        fontSize={12 * fontScale}
      >
        {secondLine}
      </text>
    </g>
  );
};

interface PieChartData {
  name: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartData[];
  title?: string;
  height?: number | string;
  includeLegend?: boolean;
  hideHeader?: boolean;
  headerClassName?: string;
  cardClassName?: string;
  contentClassName?: string;
  headerIcon?: ReactNode;
  labelFontScale?: number;
  hideLabels?: boolean;
  hideTooltip?: boolean;
}

export function PieChartComponent({
  data,
  title = "Spending by Category",
  height = 300,
  includeLegend = true,
  hideHeader = false,
  headerClassName,
  cardClassName,
  contentClassName,
  headerIcon,
  labelFontScale = 1,
  hideLabels = false,
  hideTooltip = false,
}: PieChartProps) {
  const totalValue = data.reduce((sum, item) => sum + (item.value ?? 0), 0);
  const labelRenderer = hideLabels
    ? undefined
    : (props: any) => renderLabel(props, labelFontScale);

  return (
    <Card className={cn(cardClassName)}>
      {!hideHeader && (
        <CardHeader className={headerClassName}>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            {headerIcon ?? <PieChartIcon className="h-5 w-5" />}
            {title}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={cn(contentClassName)}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={hideLabels ? undefined : labelRenderer}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            {!hideTooltip && (
              <Tooltip
                formatter={(value: number | string, _name: string, entry: any) => {
                  const numericValue = typeof value === "number" ? value : Number(value) || 0;
                  const percentage =
                    totalValue > 0 ? `${((numericValue / totalValue) * 100).toFixed(0)}%` : "0%";
                  const sliceName = entry?.name ?? "";
                  const labelPrefix = sliceName ? `${sliceName}: ` : "";
                  return [`${labelPrefix}$${numericValue.toLocaleString()} (${percentage})`, ""];
                }}
                separator=""
              />
            )}
            {includeLegend && <Legend margin={{ top: 50 }}/>}
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
